import numeral from 'numeral';

// ----------------------------------------------------------------------

export function fNumber(number) {
  return numeral(number).format();
}

export const fShortCurrency = (value, others) =>
  new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    compactDisplay: 'short',
    style: 'currency',
    currency: 'BRL',
    ...others,
  }).format(value);

export function fCurrency(number) {
  if (!number) return 'R$ 0,00';
  const format = number.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });

  return format;
}

export function fPercent(value, format, replace = '.00') {
  if (value === 0) return '0%';
  const number = value ? numeral(Number(value) / 100).format(format ? `${format}%` : '0.0%') : '';
  return result(number, replace).replace(/\./, ',');
}

export function fShortenNumber(number) {
  const format = number ? numeral(number).format('0.00a') : '';

  return result(format, '.00');
}

export function fData(number) {
  const format = number ? numeral(number).format('0.0 b') : '';

  return result(format, '.0');
}

function result(format, key = '.00') {
  const isInteger = format.includes(key);

  return isInteger ? format.replace(key, '') : format;
}

export function fCpf(number) {
  const pattern = /(\d{3})(\d{3})(\d{3})(\d{2})/;

  return (number || '').toString().replace(pattern, '$1.$2.$3-$4');
}

export function fCnpj(number) {
  const pattern = /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/;

  return (number || '').toString().replace(pattern, '$1.$2.$3/$4-$5');
}

export function fPhone(number) {
  const cleaned = `${number || ''}`.replace(/\D/g, '');

  const formats = [
    {
      pattern: /^(\d{2})(\d{1})(\d{4})(\d{4})$/,
      mask: '($1) $2 $3-$4',
    },
    {
      pattern: /^(\d{2})(\d{4})(\d{4})$/,
      mask: '($1) $2-$3',
    },
  ];

  const format = formats.find((f) => f.pattern.test(cleaned));

  if (!format) {
    return number;
  }

  const { mask } = format;

  return cleaned.replace(format.pattern, mask);
}

export function formatPercentage(number = 0) {
  return `${((number * 100) / 100).toString().replace(/\./, ',')}%`;
}

export const FLAG_BY_DDI = {
  55: { src: '/assets/icons/flags/ic_br.svg', alt: 'BR', label: 'BR' },
  351: { src: '/assets/icons/flags/ic_pt.svg', alt: 'PT', label: 'PT' },
};

export function parseIntlPhone(raw) {
  const digits = String(raw || '')
    .replace(/\D/g, '')
    .replace(/^00/, '');
  if (!digits)
    return {
      countryCode: '55',
      nationalDigits: '',
      nationalFormatted: '',
      flag: FLAG_BY_DDI['55'],
    };

  if (digits.startsWith('55') && digits.length >= 12) {
    const national = digits.slice(2);
    const ddd = national.slice(0, 2);
    const number = national.slice(2);
    const isCell = number.length === 9;
    const partA = isCell ? number.slice(0, 5) : number.slice(0, 4);
    const partB = isCell ? number.slice(5) : number.slice(4);
    const formatted = number ? `${ddd} ${partA}${partB ? `-${partB}` : ''}` : '';
    return {
      countryCode: '55',
      nationalDigits: national,
      nationalFormatted: formatted,
      flag: FLAG_BY_DDI['55'],
    };
  }

  if (digits.startsWith('351') && digits.length >= 12) {
    const national = digits.slice(3);
    const a = national.slice(0, 3);
    const b = national.slice(3, 6);
    const c = national.slice(6, 9);
    const formatted = [a, b, c].filter(Boolean).join(' ');
    return {
      countryCode: '351',
      nationalDigits: national,
      nationalFormatted: formatted,
      flag: FLAG_BY_DDI['351'],
    };
  }

  const try2 = digits.length > 2 ? { cc: digits.slice(0, 2), restLen: digits.length - 2 } : null;
  const try3 = digits.length > 3 ? { cc: digits.slice(0, 3), restLen: digits.length - 3 } : null;

  let cc = digits[0];
  let national = digits.slice(1);
  if (try2 && try2.restLen >= 6 && try2.restLen <= 12) {
    ({ cc } = try2);
    national = digits.slice(2);
  }

  if (try3 && try3.restLen >= 6 && try3.restLen <= 12) {
    ({ cc } = try3);
    national = digits.slice(3);
  }

  return {
    countryCode: cc,
    nationalDigits: national,
    nationalFormatted: national,
    flag: FLAG_BY_DDI[cc],
  };
}

export const FALLBACK_COUNTRIES = [
  { name: 'Brasil', code: 'BR', emoji: '🇧🇷', dial_code: '+55' },
  { name: 'United States', code: 'US', emoji: '🇺🇸', dial_code: '+1' },
  { name: 'Portugal', code: 'PT', emoji: '🇵🇹', dial_code: '+351' },
];

export function maskBR(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  return d.length >= 11 ? '(99) 9 9999-9999' : '(99) 9999-9999';
}

export function maskUS() {
  return '(999) 999-9999';
}

export function maskGeneric(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  if (d.length <= 6) return '999 999';
  if (d.length <= 9) return '999 999 999';
  if (d.length <= 12) return '999 999 999 999';
  return '999 999 999 999 999';
}

export function resolveMaskByDial(dial, digits) {
  if (dial === '+55') return maskBR(digits);
  if (dial === '+1') return maskUS();
  return maskGeneric(digits);
}

export function unicodeToEmoji(unicodeStr) {
  if (!unicodeStr) return '';
  return unicodeStr
    .split(' ')
    .map((u) => String.fromCodePoint(parseInt(u.replace('U+', ''), 16)))
    .join('');
}

/**
 * Converte um número de telefone para o formato E.164 (apenas dígitos, sem +)
 * @param {string} phoneNumber - Número de telefone em qualquer formato
 * @returns {string|null} - Número E.164 ou null se inválido
 */
export function toE164(phoneNumber) {
  if (!phoneNumber) return null;

  // Remove todos os caracteres não numéricos
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Valida E.164: 10-15 dígitos (sem +)
  if (cleaned.length < 10 || cleaned.length > 15) return null;

  return cleaned;
}

/**
 * Converte um número de telefone para o formato internacional do WhatsApp
 * @param {string} phoneNumber - Número de telefone em qualquer formato
 * @returns {string|null} - URL do WhatsApp ou null se inválido
 */
export function formatWhatsAppUrl(phoneNumber) {
  const e164Number = toE164(phoneNumber);

  if (!e164Number) return null;

  return `https://wa.me/${e164Number}`;
}

/**
 * Verifica se um número de telefone é válido para WhatsApp
 * @param {string} phoneNumber - Número de telefone
 * @returns {boolean} - True se válido, false caso contrário
 */
export function isValidWhatsAppNumber(phoneNumber) {
  return toE164(phoneNumber) !== null;
}
